import { AliasToken } from 'antd/es/theme/interface';
import { generate } from '@ant-design/colors';

/**
 * Colors
 */
export const colorBlue = generate('#0079bc');
export const colorGreen = generate('#8acc61');
export const colorOrange = generate('#ff8c42');
export const colorYellow = generate('#c8a61e');
export const colorGray = generate('#bfbfbf');
export const colorPink = generate('#e85c70');
export const colorPurple = generate('#be78b4');
export const colorGold = generate('#e5d195');

export const colorPrimary = colorBlue[5];
export const colorPrimaryBorder = colorBlue[2];

export const colorText = 'rgba(0, 0, 0, 0.88)';
export const colorTextSecondary = colorGray[8];
export const colorTextTertiary = colorGray[7];
export const colorTextQuaternary = colorGray[6];

export const colorTextDisabled = colorTextQuaternary;
export const colorTextPlaceholder = colorTextQuaternary;

export const colorLink = colorPrimary;
export const colorLinkActive = colorBlue[6];
export const colorLinkHover = colorBlue[2];

export const colorInfo = colorPrimary;

export const colorSuccess = colorGreen[5];
export const colorSuccessBg = colorGreen[2];
export const colorSuccessBorder = colorGreen[3];
export const colorSuccessActiveBg = colorSuccessBorder;

export const colorError = colorOrange[5];
export const colorErrorBg = '#ffe1cc';
export const colorErrorBorder = '#ffb88a';

export const colorWarning = colorYellow[5];
export const colorWarningBg = '#f3df93';
export const colorWarningBorder = '#ffd739';

export const colorAccent1 = colorPink[5];
export const colorAccent2 = colorPurple[5];
export const colorAccent3 = colorGold[5];

export const colorNeutral = '#595959';
export const colorNeutralBg = '#f0f0f0';
export const colorNeutralBorder = '#d9d9d9';

export const colorBgLayout = '#fafafa';
export const colorBorderSecondary = colorGray[3];
export const colorBorderDisabled = 'rgba(0, 0, 0, 0.04)';
export const colorBgContainer = '#ffffff';
export const colorTableOperationIcon = colorGray[7];
export const colorFillTertiary = 'rgba(0, 0, 0, 0.04)';

export const colorDraftBg = colorYellow[1];
export const colorDraftBorder = colorYellow[2];
export const colorDraftActiveBg = colorDraftBorder;

export const colorInactiveBg = colorGray[1];
export const colorInactiveBorder = colorGray[2];
export const colorInactiveActiveBg = colorInactiveBorder;

export const colorRepBorder = '#C8D5E8';
export const colorRepBg = '#E8EDF3';

export const colorLabel = '#434343';

export const colorVersion = '#FFFFFF';
export const colorVersionBg = '#000000';
export const colorVersionInactive = '#FAFAFA';
export const colorVersionInactiveBg = '#999999';

export const colorCalendarCellToday = '#f7f7f7';

/**
 * Divider
 */
export const dividerBorderColor = 'rgba(2, 2, 2, 0.15)';

/**
 * Table
 */
export const inputPaddingBlockLG = 7;
export const inputPaddingBlock = 4;
export const inputPaddingBlockSM = 0;
export const inputPaddingInlineLG = 11;
export const inputPaddingInline = 11;
export const inputPaddingInlineSM = 7;

/**
 * Table
 */
export const cellPaddingBlock = 12;
export const cellPaddingBlockMD = 8;
export const cellPaddingBlockSM = 4;
export const cellPaddingInline = 12;
export const cellPaddingInlineMD = 8;
export const cellPaddingInlineSM = 4;

/**
 * Typography
 */
export const fontFamily =
  'Helvetica Neue, Helvetica, Arial, Roboto, sans-serif';
export const fontSize = 14;
export const lineHeightPx = 22;
export const lineHeight = lineHeightPx / fontSize;
export const letterSpacing = 0.01;
export const letterSpacingStrong = 0.04;
export const fontWeightNormal = 400;
export const fontWeightStrong = 500;
export const fontSizeSM = 12;
export const lineHeightSMPx = 20;
export const lineHeightSM = lineHeightSMPx / fontSizeSM;
export const letterSpacingSM = 0.04;
export const fontSizeHeading1 = 32;
export const lineHeightHeading1 = 40 / fontSizeHeading1;
export const letterSpacingHeading1 = -0.005;
export const fontSizeHeading2 = 30;
export const lineHeightHeading2 = 40 / fontSizeHeading2;
export const letterSpacingHeading2 = 0;
export const fontSizeHeading3 = 24;
export const lineHeightHeading3 = 32 / fontSizeHeading3;
export const letterSpacingHeading3 = 0;
export const fontSizeHeading4 = 20;
export const lineHeightHeading4 = 28 / fontSizeHeading4;
export const letterSpacingHeading4 = 0;
export const fontSizeHeading5 = 16;
export const lineHeightHeading5 = 24 / fontSizeHeading5;
export const letterSpacingHeading5 = 0;

/**
 * Border radius
 */
export const borderRadiusSM = 4;
export const borderRadius = 6;
export const borderRadiusLG = 8;
export const borderRadiusXL = 8;

/**
 * Box shadow
 */
export const boxShadowSecondary = [
  '0 6px 16px rgba(0, 0, 0, 0.08)',
  '0 3px 6px rgba(0, 0, 0, 0.12)',
  '0 9px 28px rgba(0, 0, 0, 0.05)',
];

/**
 * padding
 */
export const paddingSM = 12;
export const controlPaddingHorizontal = 12;

/**
 * Dropdown
 */
export const dropdownPaddingBlock = 5;

/**
 * Radio
 */
export const radioButtonPaddingInline = 15;

/**
 * Button and Inputs
 */
export const controlHeight = 32;

export const token = {
  colorPrimary,
  colorPrimaryBorder,
  colorTextSecondary,
  colorTextTertiary,
  colorTextQuaternary,
  colorTextDisabled,
  colorTextPlaceholder,
  colorLink,
  colorError,
  colorErrorBg,
  colorErrorBorder,
  colorSuccess,
  colorSuccessBg,
  colorSuccessBorder,
  colorWarning,
  colorWarningBg,
  colorWarningBorder,
  colorInfo,
  colorLinkHover,
  colorLinkActive,
  colorBorderSecondary,
  colorBgLayout,
  fontFamily,
  fontSize,
  lineHeight,
  fontSizeSM,
  lineHeightSM,
  fontSizeHeading1,
  lineHeightHeading1,
  fontSizeHeading2,
  lineHeightHeading2,
  fontSizeHeading3,
  lineHeightHeading3,
  fontSizeHeading4,
  lineHeightHeading4,
  fontSizeHeading5,
  lineHeightHeading5,
  fontWeightStrong,
  borderRadiusSM,
  borderRadius,
  borderRadiusLG,
  paddingSM,
  linkDecoration: 'none',
  linkHoverDecoration: 'none',
  linkFocusDecoration: 'none',
  controlPaddingHorizontal,
  controlHeight,
} as AliasToken;

export const components = {
  Alert: {
    defaultPadding: `20px 40px`,
  },
  Button: {
    fontWeight: fontWeightStrong,
  },
  Descriptions: {
    titleColor: colorTextSecondary,
    colonMarginLeft: 0,
    colonMarginRight: 10,
  },
  Menu: {
    collapsedWidth: 60,
  },
  Result: {
    titleFontSize: fontSizeHeading1,
  },
  Select: {
    optionSelectedBg: colorGray[1],
  },
  Table: {
    headerColor: colorGray[8],
    headerBg: colorGray[1],
    borderColor: colorGray[3],
    headerBorderRadius: 0,
    headerSortActiveBg: colorGray[2],
    headerSortHoverBg: colorGray[2],
    headerSplitColor: 'rgba(0, 0, 0, 0.06)',
    cellPaddingBlock,
    cellPaddingBlockMD,
    cellPaddingBlockSM,
    cellPaddingInline,
    cellPaddingInlineMD,
    cellPaddingInlineSM,
  },
  Input: {
    paddingBlock: inputPaddingBlock,
    paddingBlockLG: inputPaddingBlockLG,
    paddingBlockSM: inputPaddingBlockSM,
    paddingInline: inputPaddingInline,
    paddingInlineLG: inputPaddingInlineLG,
    paddingInlineSM: inputPaddingInlineSM,
  },
  Dropdown: {
    paddingBlock: dropdownPaddingBlock,
  },
  Radio: {
    buttonPaddingInline: radioButtonPaddingInline,
  },
};
